import { alpha, createTheme } from '@mui/material/styles';
import type { PaletteTokens } from '@myrentalmate/brand';
import { brand, palette } from '../lib/brand';

// Every color comes from the brand palette (packages/brand/brand.json). Gold (`secondary`) is
// for fills and accents only; it fails contrast as text on light backgrounds.
const scheme = (t: PaletteTokens) => ({
  palette: {
    primary: { main: t.primary, dark: t.primaryDark, light: t.primaryLight },
    secondary: { main: t.accent, dark: t.accentDark, light: t.accentLight },
    success: { main: t.success },
    error: { main: t.error },
    warning: { main: t.warning },
    info: { main: t.info },
    background: { default: t.background, paper: t.surface },
    text: { primary: t.text, secondary: t.textMuted },
    divider: t.border,
  },
});

export const theme = createTheme({
  colorSchemes: { light: scheme(palette.light), dark: scheme(palette.dark) },
  typography: { fontFamily: brand.fonts.body },
  shape: { borderRadius: 8 },
  // One table look everywhere: a shaded header, alternating rows and a highlight under the pointer,
  // so it's easy to follow a row across.
  components: {
    MuiTableHead: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiTableCell-head': {
            backgroundColor: palette.light.primaryPale,
            color: palette.light.primary,
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
            borderBottom: `2px solid ${palette.light.primaryLight}`,
            ...theme.applyStyles('dark', {
              backgroundColor: palette.dark.primaryPale,
              color: palette.dark.text,
              borderBottomColor: palette.dark.primaryLight,
            }),
          },
        }),
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiTableRow-root:nth-of-type(even)': {
            backgroundColor: alpha(palette.light.primary, 0.05),
            ...theme.applyStyles('dark', { backgroundColor: alpha(palette.dark.text, 0.05) }),
          },
          '& .MuiTableRow-root:hover': {
            backgroundColor: alpha(palette.light.primary, 0.12),
            ...theme.applyStyles('dark', { backgroundColor: alpha(palette.dark.text, 0.12) }),
          },
          '& .MuiTableRow-root:last-of-type .MuiTableCell-root': { borderBottom: 0 },
        }),
      },
    },
    MuiTableCell: { styleOverrides: { sizeSmall: { paddingTop: 8, paddingBottom: 8 } } },
  },
});
